import DeviceLogTypeBadge from '@/components/device-log/device-log-type-badge';
import { useOpenClient } from '@/hooks/use-open-client';
import DeviceLogDetailDialog from '@/components/device-log/device-log-detail-dialog';
import VbivStatus from '@/components/vbiv-status';
import type { IDeviceLog } from '@/entities/device-log.entity';
import { getDeviceLogTypeMeta } from '@/entities/device-log-type-meta';
import { cn } from '@/lib/utils';
import { CalendarClockIcon, ContainerIcon, HashIcon } from 'lucide-react';
import { memo, useCallback, type MouseEvent, useState } from 'react';
import { toast } from 'sonner';
import TimeAgo from 'react-timeago';
import russianStrings from 'react-timeago/lib/language-strings/ru';
import buildFormatter from 'react-timeago/lib/formatters/buildFormatter';

const formatter = buildFormatter(russianStrings);

function timestampsDiffer(occurredAt: string | number, createdAt: string | number) {
	const diff = Math.abs(new Date(occurredAt).getTime() - new Date(createdAt).getTime());
	return diff > 60_000;
}

const DeviceLogCard = memo(function DeviceLogCard({ deviceLog }: { deviceLog: IDeviceLog }) {
	const { openClient } = useOpenClient();
	const goToClient = useCallback(() => {
		openClient(deviceLog.clientId, {
			listType: 'deviceLogs',
			clientId: deviceLog.clientId,
			itemId: deviceLog.id,
		});
	}, [deviceLog.clientId, deviceLog.id, openClient]);
	const [detailOpen, setDetailOpen] = useState(false);
	const meta = getDeviceLogTypeMeta(deviceLog.logType);
	const showBothTimestamps = timestampsDiffer(deviceLog.occurredAt, deviceLog.createdAt);

	function handleCopy(e: MouseEvent, text: string) {
		e.stopPropagation();
		navigator.clipboard
			.writeText(text)
			.then(() => toast.success('Скопировано'))
			.catch(() => toast.error('Ошибка копирования'));
	}

	return (
		<>
			<div
				role='button'
				tabIndex={0}
				className={cn(
					'relative p-2 bg-card rounded-md flex flex-col gap-1.5 border-border border border-l-2 overflow-hidden cursor-pointer active:bg-accent/40',
					meta.borderClass,
				)}
				onClick={() => setDetailOpen(true)}
				onKeyDown={e => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						setDetailOpen(true);
					}
				}}
			>
				<div className='flex justify-between items-center gap-2'>
					<div className='flex items-center gap-2 min-w-0'>
						<DeviceLogTypeBadge logType={deviceLog.logType} />
						{deviceLog.pendingLaunch ? (
							<span className='text-xs text-amber-500 shrink-0'>Ожидает launch</span>
						) : null}
					</div>
					<div className='flex items-center gap-2 shrink-0' onClick={e => e.stopPropagation()}>
						<VbivStatus clientId={deviceLog.clientId} takeStatus={deviceLog.takeStatus} takenFrom={deviceLog.takenFrom} />
						<span className='text-xs text-muted-foreground'>
							<TimeAgo date={deviceLog.occurredAt} formatter={formatter} />
						</span>
					</div>
				</div>

				{deviceLog.comment ? (
					<span className='text-sm text-[#e89bff] truncate' onClick={e => handleCopy(e, deviceLog.comment || '')}>
						{deviceLog.comment}
					</span>
				) : null}

				<p className='text-sm leading-snug line-clamp-2 break-words'>{deviceLog.message}</p>

				<div className='flex flex-wrap gap-x-3 gap-y-1 text-sm text-muted-foreground'>
					<span className='flex gap-1 items-center'>
						<HashIcon size={14} className='opacity-40 shrink-0' />
						<button
							type='button'
							className='hover:bg-accent rounded-xs cursor-pointer select-none'
							onClick={e => {
								e.stopPropagation();
								goToClient();
							}}
						>
							#{deviceLog.clientId}
						</button>
					</span>
					{deviceLog.buildId ? (
						<span className='flex gap-1 items-center'>
							<ContainerIcon size={14} className='opacity-40 shrink-0' />
							<button
								type='button'
								className='hover:bg-accent rounded-xs cursor-pointer select-none'
								onClick={e => handleCopy(e, deviceLog.buildId)}
							>
								{deviceLog.buildId}
							</button>
						</span>
					) : null}
					{deviceLog.threadName ? <span className='truncate'>{deviceLog.threadName}</span> : null}
				</div>

				{showBothTimestamps ? (
					<span className='flex gap-1 items-center text-xs text-muted-foreground'>
						<CalendarClockIcon size={14} className='opacity-40 shrink-0' />
						<span>
							Устройство / сервер:{' '}
							<TimeAgo date={deviceLog.occurredAt} formatter={formatter} /> /{' '}
							<TimeAgo date={deviceLog.createdAt} formatter={formatter} />
						</span>
					</span>
				) : null}
			</div>

			<DeviceLogDetailDialog log={deviceLog} open={detailOpen} onOpenChange={setDetailOpen} />
		</>
	);
});

export default DeviceLogCard;
